import React, { useEffect, useRef, useState } from "react";
import { Rnd } from 'react-rnd';
import { useTranslation } from "react-i18next";
import {ASCII_CODE_A, TEXTBOX} from "../../utils/constants";
import {generateUUID, notifyError, notifyInfo} from "../../utils/helpers";
import { decodeCIParam, encodeCIParam } from "../../utils/questionUtils";
import {ReactComponent as DELETE} from "./ci_option_delete.svg";

export default function ClickableImageNew({
                                              questions=[],
                                              setQuestions,
                                              selectedQuestion,
                                              imageAlt,
                                              disableDrag=false,
                                              showOptionArea = true
}) {
    const { t } = useTranslation();
    const [isLoaded, setIsLoaded] = useState(false);
    const [imageWidth, setImageWidth] = useState(1);
    const [imageHeight, setImageHeight] = useState(1);
    const imageRef = useRef();
    const [randomId, ] = useState(() => generateUUID());


    const clickImage = (e) => {
        const EDGE = Math.min(imageWidth / 2, imageHeight / 2, 64);

        const xCoordinate = e.nativeEvent.offsetX;
        const yCoordinate = e.nativeEvent.offsetY;
        const newOption =  {
            text: "",
            x: encodeCIParam(xCoordinate - EDGE / 2, imageWidth),
            y: encodeCIParam(yCoordinate - EDGE / 2, imageHeight),
            width: encodeCIParam(EDGE, imageWidth),
            height: encodeCIParam(EDGE, imageHeight)
        };

        const questionsCopy = JSON.parse(JSON.stringify(questions));
        const {serialNo} = selectedQuestion;
        const curQuestion = questionsCopy.find(q => q.serialNo === serialNo);
        if (curQuestion.optionsMap.length >= 26) {
            notifyInfo(t('questionBuilder.maxOptionsReachedInfo'));
            return;
        }


        if (xCoordinate + EDGE / 2 > imageWidth
            || xCoordinate - EDGE / 2 < 0
            || yCoordinate + EDGE / 2 > imageHeight
            || yCoordinate - EDGE / 2 < 0) {
            // notifyError("The pointed space is not enough to add a new Area");
            setAdjustedOption(EDGE, newOption);
            return;
        }
        // for(let i = 0; i < curQuestion.optionsMap.length; i++) {
        //     for(let j = curQuestion.optionsMap.length - 1; j > i; j--) {
        //         if (hasIntersection(curQuestion.optionsMap[i], curQuestion.optionsMap[j])) {
        //             notifyError("Two different areas can not be overlapped");
        //             return;
        //         }
        //     }
        // }

        const hasIntersection = hasIntersectionWithCurOptions(newOption, curQuestion.optionsMap);
        if (!hasIntersection) {
            curQuestion.optionsMap.push(newOption);
            setQuestions(questionsCopy);
            return;
        }
        setAdjustedOption(EDGE, newOption);
    }


    const setAdjustedOption = (edge, newOption) => {
        const questionsCopy = JSON.parse(JSON.stringify(questions));
        const {serialNo} = selectedQuestion;
        const curQuestion = questionsCopy.find(q => q.serialNo === serialNo);
        const options = curQuestion.optionsMap;

        let {x, y, width, height} = newOption;
        x = decodeCIParam(x, imageWidth);
        y = decodeCIParam(y, imageHeight);


        let adjustedX = 0;
        let adjustedY = 0;
        const availableArr = [];
        for (let offsetX = -1 * edge / 2; offsetX <= edge / 2; offsetX++) {
            for (let offsetY = -1 * edge / 2; offsetY <= edge / 2; offsetY++) {
                adjustedX = x + offsetX;
                adjustedY = y + offsetY;
                if (adjustedX + edge > imageWidth
                    || adjustedX < 0
                    || adjustedY + edge > imageHeight
                    || adjustedY < 0) {
                    continue;
                }

                const adjustedOpt = {
                    x: encodeCIParam(adjustedX, imageWidth),
                    y: encodeCIParam(adjustedY, imageHeight),
                    width,
                    height,
                    distance: (x - adjustedX) ** 2 + (y - adjustedY) ** 2
                };
                if (!hasIntersectionWithCurOptions(adjustedOpt, options)) {
                    availableArr.push(adjustedOpt);
                }
            }
        }

        if (!availableArr.length) {
            return;
        }

        const adjustedOpt = availableArr.sort((a, b) => a.distance - b.distance)[0];
        delete adjustedOpt.distance;

        options.push(adjustedOpt);
        setQuestions(questionsCopy);
    };



    const hasIntersectionWithCurOptions = (newOption, options) => {
        for(let i = 0; i < options.length; i++) {
            if (hasIntersection(newOption, options[i])) {
                return true;
            }
        }

        return false;
    }

    const onResize = (...[, , target, delta, position]) => {
        const eleId = target.id

        const questionsCopy = JSON.parse(JSON.stringify(questions));
        const {serialNo} = selectedQuestion;
        const curQuestion = questionsCopy.find(q => q.serialNo === serialNo);
        let arr = eleId.split("-");
        const area = curQuestion.optionsMap[arr[arr.length - 1]];

        area.width = encodeCIParam(area.width * imageWidth + delta.width, imageWidth);
        area.height = encodeCIParam(area.height * imageHeight + delta.height, imageHeight);
        area.x = encodeCIParam(position.x, imageWidth);
        area.y = encodeCIParam(position.y, imageHeight);

        for(let i = 0; i < curQuestion.optionsMap.length; i++) {
            for(let j = curQuestion.optionsMap.length - 1; j > i; j--) {
                if (hasIntersection(curQuestion.optionsMap[i], curQuestion.optionsMap[j])) {
                    notifyError(t('questionBuilder.areaOverlapError'));
                    return;
                }
            }
        }
        setQuestions(questionsCopy);
    }

    const onDragStart = (e, data) => {
        e.preventDefault();
        const eleId = data.node.id;
        let ele = document.querySelector(`#${eleId}`);
        if (ele) {
            ele.style.cursor = 'grabbing';
        }
    }

    const onDragStop = (e, data) => {
        e.preventDefault();
        const eleId = data.node.id;
        let ele = document.querySelector(`#${eleId}`);
        if (ele) {
            ele.style.cursor = 'grab';
        }

        const questionsCopy = JSON.parse(JSON.stringify(questions));
        const {serialNo} = selectedQuestion;
        const curQuestion = questionsCopy.find(q => q.serialNo === serialNo);
        let arr = eleId.split("-");
        const area = curQuestion.optionsMap[arr[arr.length - 1]];

        area.x = encodeCIParam(data.x, imageWidth);
        area.y = encodeCIParam(data.y, imageHeight);

        for(let i = 0; i < curQuestion.optionsMap.length; i++) {
            for(let j = curQuestion.optionsMap.length - 1; j > i; j--) {
                if (hasIntersection(curQuestion.optionsMap[i], curQuestion.optionsMap[j])) {
                    // notifyError("Two different areas can not be overlapped");
                    return;
                }
            }
        }
        setQuestions(questionsCopy);
    }

    const onLoad = (e) => {
        setIsLoaded(true);
        setImageWidth(e.target.width);
        setImageHeight(e.target.height);
    }


    const hasIntersection = (area1, area2) => {
        const xStart1 = area1.x,
            xEnd1 = area1.x + area1.width,
            yStart1 = area1.y,
            yEnd1 = area1.y + area1.height,
            xStart2 = area2.x,
            xEnd2 = area2.x + area2.width,
            yStart2 = area2.y,
            yEnd2 = area2.y + area2.height;
        const isOutSide = (xEnd1 < xStart2) // area1 is on the left side of area2
            || (xStart1 > xEnd2)  // area1 is on the right side of area2
            || (yEnd1 < yStart2) // area1 is on the top side of area2
            || (yStart1 > yEnd2);// area1 is on the bottom side of area2


        return !isOutSide;
    }

    const onMouseEnter = (idx) => {
        if (disableDrag) {
            return;
        }

        document.getElementById(`delete-option-icon-${randomId}-${idx}`).style.display = "block";
        for (let i = 0; i < selectedQuestion.optionsMap.length; i++) {
            if (i === idx) {
                document.getElementById(`option-area-${randomId}-${i}`).style.zIndex = "1";
            } else {
                document.getElementById(`option-area-${randomId}-${i}`).style.zIndex = "0";
            }
        }
    }


    const onMouseLeave = (idx) => {
        if (disableDrag) {
            return;
        }

        document.getElementById(`delete-option-icon-${randomId}-${idx}`).style.display = "none";
        for (let i = 0; i < selectedQuestion.optionsMap.length; i++) {
            document.getElementById(`option-area-${randomId}-${i}`).style.zIndex = "0";
        }
    }

    const onDelete = (idx) => {
        const questionsCopy = JSON.parse(JSON.stringify(questions));
        const {serialNo} = selectedQuestion;
        const curQuestion = questionsCopy.find(q => q.serialNo === serialNo);
        const optionsMap = curQuestion.optionsMap;
        if (idx >= optionsMap.length) {
            return;
        }
        optionsMap.splice(idx, 1);
        const correctAnswers = curQuestion.correctAnswers;
        if (!!correctAnswers && !!correctAnswers.length && correctAnswers.includes(idx)) {
            const index = correctAnswers.indexOf(idx);
            correctAnswers.splice(index, 1);
        }
        setQuestions(questionsCopy);
    }

      
    useEffect(() => {
        const resizeObserver = new ResizeObserver(() => {
          if (imageRef.current) {
            setImageWidth(imageRef.current.clientWidth)
            setImageHeight(imageRef.current.clientHeight)
          }
        });
    
        if (imageRef.current) {
          resizeObserver.observe(imageRef.current);
    
          return () => {
            resizeObserver.disconnect();
          };
        }
      }, []);

    if (!selectedQuestion || !selectedQuestion.imageURL) {
        return <></>;
    }



    return(
        <>
            <img
                tabIndex={0}
                role={TEXTBOX}
                ref={imageRef}
                onClick={clickImage}
                src={`${selectedQuestion.imageURL}`}
                alt={imageAlt ? imageAlt : t('accessibility.defaultImageAlt')}
                onLoad={onLoad}
                style={{ maxWidth: "100%", maxHeight: "100%" }}
            />
            {isLoaded && showOptionArea && selectedQuestion.optionsMap.map((area, idx) => {
                const EDGE = Math.min(imageWidth, imageHeight);

                return (
                    <Rnd
                        tabIndex={0}
                        role={TEXTBOX}
                        aria-label={t('accessibility.optionLabel') + String.fromCharCode(idx + ASCII_CODE_A)}
                        key={idx}
                        id={`option-area-${randomId}-${idx}`}
                        style={{cursor: disableDrag ? "default" : "grab"}}
                        size={{
                            width: decodeCIParam(area.width, imageWidth),
                            height: decodeCIParam(area.height, imageHeight)
                        }}
                        position={{
                            x: decodeCIParam(area.x, imageWidth),
                            y: decodeCIParam(area.y, imageHeight)
                        }}
                        disableDragging={disableDrag}
                        enableResizing={disableDrag ? false : {
                            bottom: true,
                            bottomLeft: false,
                            bottomRight: false,
                            left: true,
                            right: true,
                            top: false,
                            topLeft: false,
                            topRight: false,
                        }}
                        resizeHandleStyles={{
                            bottom: {cursor: "n-resize"},
                            left: {cursor: "e-resize"},
                            right: {cursor: "e-resize"},
                        }}
                        bounds={"parent"}
                        onResizeStop={onResize}
                        onDragStart={onDragStart}
                        onDragStop={onDragStop}
                        onMouseEnter={() => onMouseEnter(idx)}
                        onMouseLeave={() => onMouseLeave(idx)}
                    >
                        <div style={{
                            width: "100%",
                            height: "100%",
                            position: "relative",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            border: "solid #FDAE3C 2px",
                            borderRadius: "4px",
                            color: "#FFFFFF",
                            fontWeight: "400",
                            fontSize: "15px",
                            backgroundColor: "#FDAE3C66",
                            boxShadow: "0px 0px 5px 0px #00000026"

                        }}>
                            {String.fromCharCode(ASCII_CODE_A + idx)}
                            <DELETE
                                tabIndex={0}
                                role={"button"}
                                id={`delete-option-icon-${randomId}-${idx}`}
                                onClick={() => onDelete(idx)}
                                style={{
                                    width: EDGE > 600 ? 32 : 16,
                                    height: EDGE > 600 ? 32 : 16,
                                    display: "none",
                                    position: "absolute",
                                    zIndex: 1000000000,
                                    top: EDGE > 600 ? -16 : -8,
                                    right: EDGE > 600 ? "calc(50% - 16px)" : "calc(50% - 8px)",
                                    cursor: "pointer",
                                }}
                            />
                        </div>
                    </Rnd>
                );
            })}
        </>
    );
}